"use client";

import { useId, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useDebouncedValue } from "@/features/playground/lib/use-debounced-value";
import { fetchInstitutionSuggestions, SELF_TAUGHT } from "../api";

type Labels = {
  placeholder: string;
  /** The one-click answer for somebody who studies on their own. */
  selfTaught: string;
};

/**
 * `EX-IDN04` (ADR-0088): where the person studies or works. Free text, with
 * suggestions after two letters (the curated Bangladesh list first, then what
 * other people already typed), so one university is stored one way. Arrows
 * and Enter pick a suggestion; typing on keeps the text as written.
 * "Self-taught" is an answer of its own.
 */
export function InstitutionInput({
  id,
  value,
  onChange,
  labels,
  maxLength = 150,
}: {
  id: string;
  value: string;
  onChange: (next: string) => void;
  labels: Labels;
  maxLength?: number;
}) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const query = useDebouncedValue(value.trim(), 250);

  const { data } = useQuery({
    queryKey: ["institutions", "suggest", query],
    queryFn: () => fetchInstitutionSuggestions(query),
    enabled: open && query.length >= 2,
    staleTime: 5 * 60_000,
  });
  const items = (open && query.length >= 2 ? data ?? [] : []).filter((item) => item.name !== value.trim());

  function pick(name: string) {
    onChange(name);
    setOpen(false);
    setActive(-1);
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <Input
          id={id}
          role="combobox"
          aria-expanded={items.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={active >= 0 && items[active] ? `${listId}-${active}` : undefined}
          autoComplete="organization"
          maxLength={maxLength}
          placeholder={labels.placeholder}
          value={value}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown" && items.length > 0) {
              e.preventDefault();
              setActive((current) => Math.min(items.length - 1, current + 1));
            } else if (e.key === "ArrowUp" && items.length > 0) {
              e.preventDefault();
              setActive((current) => Math.max(0, current - 1));
            } else if (e.key === "Enter" && active >= 0 && items[active]) {
              e.preventDefault();
              pick(items[active].name);
            } else if (e.key === "Escape") {
              setOpen(false);
            }
          }}
        />
        {items.length > 0 ? (
          <ul
            id={listId}
            role="listbox"
            className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-[var(--radius)] border border-neutral-200 bg-white py-1 shadow-[0_8px_30px_rgba(0,0,0,0.06)]"
          >
            {items.map((item, index) => (
              <li
                key={`${item.source}:${item.name}`}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={index === active}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(item.name)}
                onMouseEnter={() => setActive(index)}
                className={cn(
                  "flex cursor-pointer items-baseline justify-between gap-3 px-3 py-2 text-sm text-neutral-800",
                  index === active && "bg-neutral-100",
                )}
              >
                <span>{item.name === SELF_TAUGHT ? labels.selfTaught : item.name}</span>
                {item.short ? <span className="shrink-0 text-xs font-semibold text-neutral-500">{item.short}</span> : null}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      {value.trim() !== SELF_TAUGHT ? (
        <Button type="button" variant="outline" className="self-start" onClick={() => pick(SELF_TAUGHT)}>
          {labels.selfTaught}
        </Button>
      ) : null}
    </div>
  );
}
